import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';

const SectionDetailModal = ({ isOpen, onClose, sectionType, data, projectName, projectImageService }) => {
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [imageSignedUrls, setImageSignedUrls] = useState({});

  const sectionConfig = {
    notes: {
      title: 'Project Notes',
      icon: 'FileText',
      columns: ['Title', 'Content', 'Created By', 'Date'],
      getRow: (item) => [
        item?.title || '(No title)',
        item?.content || '',
        item?.created_by_profile?.full_name || item?.created_by_profile?.email || 'Unknown',
        item?.created_at ? format(new Date(item.created_at), 'MM/dd/yyyy h:mm a') : '',
      ],
    },
    documents: {
      title: 'Project Documents',
      icon: 'FolderOpen',
      columns: ['Name', 'Type', 'Note', 'Source', 'Size', 'Date'],
      getRow: (item) => [
        item?.name || '(No name)',
        item?.is_link ? 'Link' : 'File',
        item?.document_note || '',
        item?.is_link ? (item?.link_url || '') : (item?.document_source || ''),
        item?.file_size ? formatFileSize(item?.file_size) : '',
        item?.created_at ? format(new Date(item.created_at), 'MM/dd/yyyy h:mm a') : '',
      ],
    },
    images: {
      title: 'Project Images',
      icon: 'Image',
      columns: ['Title', 'Note', 'URL / Storage', 'Date'],
      getRow: (item) => [
        item?.title || '(No title)',
        item?.image_note || '',
        item?.is_upload ? '(Uploaded file)' : (item?.image_url || ''),
        item?.created_at ? format(new Date(item.created_at), 'MM/dd/yyyy h:mm a') : '',
      ],
    },
    changeOrders: {
      title: 'Change Orders',
      icon: 'ClipboardList',
      columns: ['Title', 'Description', 'Impact', 'Created By', 'Date'],
      getRow: (item) => [
        item?.title || '(No title)',
        item?.description || '',
        item?.impact || '',
        item?.created_by_profile?.full_name || item?.created_by_profile?.email || 'Unknown',
        item?.created_at ? format(new Date(item.created_at), 'MM/dd/yyyy h:mm a') : '',
      ],
    },
  };

  const config = sectionConfig?.[sectionType] || sectionConfig?.notes;

  useEffect(() => {
    if (isOpen && sectionType === 'images' && data?.length > 0 && projectImageService) {
      const loadSignedUrls = async () => {
        const urls = {};
        for (const img of data) {
          if (img?.is_upload && img?.file_path) {
            try {
              const signed = await projectImageService?.getSignedUrl(img?.file_path);
              urls[img.id] = signed;
            } catch {}
          } else if (img?.image_url) {
            urls[img.id] = img?.image_url;
          }
        }
        setImageSignedUrls(urls);
      };
      loadSignedUrls();
    }
  }, [isOpen, sectionType, data, projectImageService]);

  useEffect(() => {
    if (isOpen) {
      setEmailSubject(`${config?.title} – ${projectName || 'Project'}`);
      setEmailTo('');
      setShowEmailForm(false);
      setEmailStatus(null);
    }
  }, [isOpen, sectionType, projectName]);

  if (!isOpen) return null;

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const exportToCSV = () => {
    const headers = config?.columns;
    const rows = (data || [])?.map(config?.getRow);
    const csvContent = [headers, ...rows]?.map((row) =>
        row?.map((cell) => `"${String(cell ?? '')?.replace(/"/g, '""')}"`)?.join(',')
      )?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${config?.title?.replace(/\s+/g, '_')}_${projectName?.replace(/\s+/g, '_') || 'Project'}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const headers = config?.columns;
      const rows = (data || [])?.map(config?.getRow);
      const wsData = [headers, ...rows];
      const ws = XLSX?.utils?.aoa_to_sheet(wsData);
      const wb = XLSX?.utils?.book_new();
      XLSX?.utils?.book_append_sheet(wb, ws, config?.title?.substring(0, 31));
      XLSX?.writeFile(wb, `${config?.title?.replace(/\s+/g, '_')}_${projectName?.replace(/\s+/g, '_') || 'Project'}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  };

  const exportToPDF = async () => {
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new jsPDF({ orientation: 'landscape' });
      doc?.setFontSize(14);
      doc?.text(`${config?.title} – ${projectName || 'Project'}`, 14, 16);
      doc?.setFontSize(9);
      doc?.text(`Exported: ${format(new Date(), 'MM/dd/yyyy h:mm a')}`, 14, 23);
      const headers = [config?.columns];
      const rows = (data || [])?.map(config?.getRow);
      autoTable(doc, {
        head: headers,
        body: rows,
        startY: 28,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
      });
      doc?.save(`${config?.title?.replace(/\s+/g, '_')}_${projectName?.replace(/\s+/g, '_') || 'Project'}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo?.trim()) return;
    setSendingEmail(true);
    setEmailStatus(null);
    try {
      const headers = config?.columns;
      const rows = (data || [])?.map(config?.getRow);
      const body = encodeURIComponent(
        `Please find the ${config?.title} for project "${projectName}" below:\n\n` +
        [headers?.join('\t'), ...rows?.map((r) => r?.join('\t'))]?.join('\n') +
        `\n\nExported on ${format(new Date(), 'MM/dd/yyyy h:mm a')}`
      );
      const subject = encodeURIComponent(emailSubject);
      const mailto = `mailto:${encodeURIComponent(emailTo)}?subject=${subject}&body=${body}`;
      window.open(mailto, '_blank');
      setEmailStatus('success');
      setTimeout(() => {
        setShowEmailForm(false);
        setEmailStatus(null);
      }, 2000);
    } catch (err) {
      setEmailStatus('error');
    } finally {
      setSendingEmail(false);
    }
  };

  /* ── Mobile card renderer for table sections ── */
  const MobileCard = ({ item, idx }) => {
    const row = config?.getRow(item);
    return (
      <div className="bg-background border border-border rounded-lg p-3 space-y-1.5">
        {config?.columns?.map((col, colIdx) => {
          const cell = row?.[colIdx];
          if (!cell) return null;
          return (
            <div key={col} className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{col}</span>
              <span className={`text-xs break-words ${colIdx === 0 ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                {cell}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[500] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
      {/* Modal container — full-screen sheet on mobile, centered card on sm+ */}
      <div className="bg-card border border-border sm:rounded-xl shadow-2xl w-full sm:max-w-5xl flex flex-col
                      h-[92dvh] sm:h-auto sm:max-h-[90vh] rounded-t-2xl sm:rounded-xl">

        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pt-2 pb-0 sm:hidden shrink-0">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 shrink-0">
              <Icon name={config?.icon} size={16} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm sm:text-base font-semibold text-foreground truncate">{config?.title}</h2>
              <p className="text-xs text-muted-foreground truncate">{projectName} · {data?.length || 0} item{data?.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {/* Touch-friendly close button */}
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-2 rounded-md hover:bg-muted shrink-0 min-w-[40px] min-h-[40px] flex items-center justify-center">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Export Actions — scrollable row on mobile */}
        <div className="px-4 sm:px-5 py-2.5 border-b border-border bg-muted/30 shrink-0">
          <div className="flex items-center gap-2 overflow-x-auto pb-0.5 scrollbar-none">
            <span className="text-xs font-medium text-muted-foreground shrink-0">Export:</span>
            <button
              onClick={exportToPDF}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100 transition-colors shrink-0 min-h-[36px]">
              <Icon name="FileText" size={13} />
              PDF
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 transition-colors shrink-0 min-h-[36px]">
              <Icon name="Table" size={13} />
              Excel
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors shrink-0 min-h-[36px]">
              <Icon name="Download" size={13} />
              CSV
            </button>
            <button
              onClick={() => setShowEmailForm((prev) => !prev)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border rounded-md transition-colors shrink-0 min-h-[36px] ${
                showEmailForm
                  ? 'bg-accent text-accent-foreground border-accent'
                  : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
              }`}>
              <Icon name="Mail" size={13} />
              Email
            </button>
          </div>
        </div>

        {/* Email Form */}
        {showEmailForm && (
          <div className="px-4 sm:px-5 py-3 border-b border-border bg-purple-50/50 shrink-0">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 min-w-0">
                  <label className="block text-xs font-medium text-foreground mb-1">To</label>
                  <input
                    type="email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e?.target?.value)}
                    placeholder="recipient@example.com"
                    className="w-full px-3 py-2 text-xs border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent min-h-[40px]" />
                </div>
                <div className="flex-1 min-w-0">
                  <label className="block text-xs font-medium text-foreground mb-1">Subject</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e?.target?.value)}
                    className="w-full px-3 py-2 text-xs border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent min-h-[40px]" />
                </div>
              </div>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailTo?.trim()}
                className="flex items-center justify-center gap-1.5 w-full sm:w-auto sm:self-end px-4 py-2.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 min-h-[40px]">
                <Icon name={sendingEmail ? 'Loader2' : 'Send'} size={13} className={sendingEmail ? 'animate-spin' : ''} />
                {sendingEmail ? 'Opening…' : 'Send'}
              </button>
            </div>
            {emailStatus === 'success' && (
              <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                <Icon name="CheckCircle2" size={12} /> Email client opened successfully.
              </p>
            )}
            {emailStatus === 'error' && (
              <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                <Icon name="AlertCircle" size={12} /> Failed to open email client.
              </p>
            )}
          </div>
        )}

        {/* Content — independently scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-5 py-4">
          {!data || data?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                <Icon name={config?.icon} size={24} className="text-muted-foreground/40" />
              </div>
              <p className="text-sm text-muted-foreground">No {config?.title?.toLowerCase()} found</p>
            </div>
          ) : sectionType === 'images' ? (
            /* Image grid view */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data?.map((img) => {
                const displayUrl = imageSignedUrls?.[img?.id] || img?.image_url || '';
                return (
                  <div key={img?.id} className="bg-background border border-border rounded-lg overflow-hidden">
                    <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
                      {displayUrl ? (
                        <img
                          src={displayUrl}
                          alt={img?.title || 'Project image'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="w-full h-full items-center justify-center text-muted-foreground/40 flex-col gap-1"
                        style={{ display: displayUrl ? 'none' : 'flex' }}>
                        <Icon name="ImageOff" size={28} />
                        <span className="text-xs">No image</span>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-foreground truncate">{img?.title || '(No title)'}</p>
                      {img?.image_note && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{img?.image_note}</p>
                      )}
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {img?.created_at ? format(new Date(img.created_at), 'MM/dd/yyyy') : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Table view — desktop table / mobile card stack */
            <div>
              {/* Desktop table (hidden on mobile) */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      {config?.columns?.map((col) => (
                        <th
                          key={col}
                          className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data?.map((item, idx) => {
                      const row = config?.getRow(item);
                      return (
                        <tr
                          key={item?.id || idx}
                          className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          {row?.map((cell, cellIdx) => (
                            <td
                              key={cellIdx}
                              className={`px-3 py-2.5 text-xs text-foreground align-top ${
                                cellIdx === 0 ? 'font-medium' : 'text-muted-foreground'
                              }`}
                              style={{ maxWidth: cellIdx === 1 ? '300px' : undefined }}>
                              <span className={cellIdx === 1 ? 'line-clamp-3 block' : ''}>
                                {cell || <span className="text-muted-foreground/40 italic">—</span>}
                              </span>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile card stack (visible only on mobile) */}
              <div className="flex flex-col gap-3 sm:hidden">
                {data?.map((item, idx) => (
                  <MobileCard key={item?.id || idx} item={item} idx={idx} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-t border-border bg-muted/20 shrink-0">
          <span className="text-xs text-muted-foreground">
            {data?.length || 0} record{data?.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-xs font-medium bg-muted text-foreground border border-border rounded-md hover:bg-muted/80 transition-colors min-h-[40px]">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionDetailModal;
